// Copyright (c) 2026 Goffy. All Rights Reserved.

/**
 * Play-order math for rounds that did not start on hole 1 (back-nine and shotgun starts).
 * Play order is startHole..holesCount then wrapping 1..startHole-1. Every helper treats a
 * missing/invalid start hole as 1, which reproduces today's ascending-order behaviour.
 */

const normalizeStart = (startHole: number | null | undefined, holesCount: number): number =>
  startHole != null && Number.isInteger(startHole) && startHole >= 1 && startHole <= holesCount
    ? startHole
    : 1;

/** Zero-based position of a hole in the round's play order. */
export const playIndex = (
  hole: number,
  startHole: number | null | undefined,
  holesCount: number,
): number => {
  if (holesCount <= 0) return hole;
  const start = normalizeStart(startHole, holesCount);
  if (hole < 1 || hole > holesCount) return holesCount + Math.max(hole, 0);
  return (((hole - start) % holesCount) + holesCount) % holesCount;
};

/** The hole played after `hole`, or null when `hole` is the last hole in play order. */
export const nextHole = (
  hole: number,
  startHole: number | null | undefined,
  holesCount: number,
): number | null => {
  if (holesCount <= 0 || hole < 1 || hole > holesCount) return null;
  const start = normalizeStart(startHole, holesCount);
  const next = hole >= holesCount ? 1 : hole + 1;
  return next === start ? null : next;
};

/** The hole played before `hole`, or null when `hole` is the round's start hole. */
export const prevHole = (
  hole: number,
  startHole: number | null | undefined,
  holesCount: number,
): number | null => {
  if (holesCount <= 0 || hole < 1 || hole > holesCount) return null;
  const start = normalizeStart(startHole, holesCount);
  if (hole === start) return null;
  return hole <= 1 ? holesCount : hole - 1;
};

export const isFirstHole = (
  hole: number,
  startHole: number | null | undefined,
  holesCount: number,
): boolean => holesCount > 0 && hole === normalizeStart(startHole, holesCount);

export const isLastHole = (
  hole: number,
  startHole: number | null | undefined,
  holesCount: number,
): boolean => playIndex(hole, startHole, holesCount) === holesCount - 1;

/**
 * The numeric wrap point: the round started past hole 1, so hole `holesCount` continues on
 * hole 1 instead of finishing the round.
 */
export const isWrapHole = (
  hole: number,
  startHole: number | null | undefined,
  holesCount: number,
): boolean => hole === holesCount && normalizeStart(startHole, holesCount) > 1;

/** Every hole of the round in the order it is played. */
export const playOrder = (
  startHole: number | null | undefined,
  holesCount: number,
): number[] => {
  if (holesCount <= 0) return [];
  const start = normalizeStart(startHole, holesCount);
  return Array.from({ length: holesCount }, (_, i) => ((start - 1 + i) % holesCount) + 1);
};
